import { FC, useEffect, useState, useRef } from 'react';
import styled from '@emotion/styled';
import { Typography, FormControlLabel, Checkbox, TextField, withStyles } from '@material-ui/core';
import SpeculationRule from '../../../../entity/SpeculationRule';
import { Organization } from '@kaizenplatform/intelligence-entity';
import { PaperButton } from '../../primitive/Page';
import { PaperContent, PaperDivider, PaperItem, StyledPaper } from '../../primitive/Paper';
import StyledLoading from './StyledLoading';
import { COLOR_TEXT_MAIN } from '../../../../theme/colors';

type Props = {
  organization: Organization | null;
  speculationRule: SpeculationRule | null;
  speculationRuleLoading: boolean;
  getSpeculationRule: () => void;
  putSpeculationRule: (speculationRule: SpeculationRule) => void;
  onMount: () => void;
};

const LinkPrefetchEditTab: FC<Props> = ({
  organization,
  speculationRule,
  speculationRuleLoading,
  getSpeculationRule,
  putSpeculationRule,
  onMount,
}) => {
  const [enabled, setEnabled] = useState(false);
  const [pageUrlRegexp, setPageUrlRegexp] = useState('');
  const [regexError, setRegexError] = useState(false);
  const [whitelist, setWhitelist] = useState<Array<{ id: string; value: string }>>([]);
  const [blacklist, setBlacklist] = useState<Array<{ id: string; value: string }>>([]);
  const whitelistRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const blacklistRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  useEffect(() => {
    if (organization) {
      onMount();
    }
  }, [organization]);

  useEffect(() => {
    if (speculationRule) {
      setEnabled(!!speculationRule.enabled);
      setPageUrlRegexp(speculationRule.pageUrlRegexp ?? '');
      setWhitelist(
        (speculationRule.whitelistPathPatterns ?? []).map((pattern) => ({
          id: Math.random().toString(36).substr(2, 9),
          value: pattern,
        })),
      );
      setBlacklist(
        (speculationRule.blacklistPathPatterns ?? []).map((pattern) => ({
          id: Math.random().toString(36).substr(2, 9),
          value: pattern,
        })),
      );
    }
  }, [speculationRule]);

  const removeWhitelist = (id: string) => {
    setWhitelist(whitelist.filter((item) => item.id !== id));
  };

  const removeBlacklist = (id: string) => {
    setBlacklist(blacklist.filter((item) => item.id !== id));
  };

  const handleSave = () => {
    const currentWhitelist = whitelist.map((item) => whitelistRefs.current[item.id]?.value || '').filter((v) => v);
    const currentBlacklist = blacklist.map((item) => blacklistRefs.current[item.id]?.value || '').filter((v) => v);

    putSpeculationRule(
      new SpeculationRule({
        enabled,
        pageUrlRegexp,
        whitelistPathPatterns: currentWhitelist,
        blacklistPathPatterns: currentBlacklist,
        dpState: speculationRule?.dpState || 'init',
        dpActivatedAt: speculationRule?.dpActivatedAt || null,
      }),
    );
  };

  const handleCancel = () => {
    const confirm = window.confirm('編集内容を破棄して元に戻しますか？');
    if (confirm) {
      getSpeculationRule();
    }
  };

  const validateRegex = (pattern: string) => {
    if (!pattern) return true;
    try {
      new RegExp(pattern);
      return true;
    } catch (e) {
      return false;
    }
  };

  useEffect(() => {
    setRegexError(!validateRegex(pageUrlRegexp));
  }, [pageUrlRegexp]);

  if (speculationRuleLoading) {
    return <StyledLoading />;
  }

  return (
    <StyledPaper>
      <PaperContent>
        <StyledPaperItem>
          <FormControlLabelMui
            control={<Checkbox checked={enabled} onChange={() => setEnabled(!enabled)} />}
            label="リンク先読み機能を有効にする"
          />
          <DescriptionText>
            効果の比較計測のため、条件を満たす場合でも10%の確率で機能を無効化して測定します。
          </DescriptionText>
        </StyledPaperItem>
        <StyledPaperItem>
          <SectionLabel>対象ページURL（正規表現）</SectionLabel>
          <StyledInputField
            type="text"
            value={pageUrlRegexp}
            onChange={(e) => setPageUrlRegexp(e.target.value)}
            placeholder="例: ^https://www\.example\.com/products/.*"
            error={regexError}
          />
          {regexError && <InputError>有効な正規表現を入力してください</InputError>}
          <DescriptionText>この機能を有効にするページのURLの正規表現を指定してください。</DescriptionText>
        </StyledPaperItem>
        <PaperDivider />
        <StyledPaperItem>
          <SectionLabel>先読みを有効にするリンクパターン（ホワイトリスト）</SectionLabel>
          <DescriptionText>先読みを有効にするリンクのパターンをワイルドカードで指定してください。</DescriptionText>
          <PatternList>
            {whitelist.map((item) => (
              <PatternItem key={item.id}>
                <RemoveButton onClick={() => removeWhitelist(item.id)}>−</RemoveButton>
                <PatternInput defaultValue={item.value} ref={(el) => (whitelistRefs.current[item.id] = el)} />
              </PatternItem>
            ))}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '12px' }}>
              <AddButton
                onClick={() => setWhitelist([...whitelist, { id: Math.random().toString(36).substr(2, 9), value: '' }])}
              >
                リンクパターンを追加
              </AddButton>
            </div>
          </PatternList>
        </StyledPaperItem>
        <PaperDivider />
        <StyledPaperItem>
          <SectionLabel>先読みを禁止するリンクパターン（ブラックリスト）</SectionLabel>
          <DescriptionText>先読みを禁止するリンクのパターンをワイルドカードで指定してください。</DescriptionText>
          <PatternList>
            {blacklist.map((item) => (
              <PatternItem key={item.id}>
                <RemoveButton onClick={() => removeBlacklist(item.id)}>−</RemoveButton>
                <PatternInput defaultValue={item.value} ref={(el) => (blacklistRefs.current[item.id] = el)} />
              </PatternItem>
            ))}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '12px' }}>
              <AddButton
                onClick={() => setBlacklist([...blacklist, { id: Math.random().toString(36).substr(2, 9), value: '' }])}
              >
                リンクパターンを追加
              </AddButton>
            </div>
          </PatternList>
        </StyledPaperItem>
        <PaperDivider />
        <ButtonArea>
          <CancelButton onClick={handleCancel}>編集内容を元に戻す</CancelButton>
          <SaveButton onClick={handleSave} disabled={regexError}>
            保存する
          </SaveButton>
        </ButtonArea>
      </PaperContent>
    </StyledPaper>
  );
};

// --- スタイル定義 ---
const StyledPaperItem = styled(PaperItem)`
  max-width: 1000px;
  margin: 0 auto;
`;

const SectionLabel = styled(Typography)`
  font-weight: 700;
  font-size: 15px;
  margin-bottom: 8px;
`;

const DescriptionText = styled(Typography)`
  color: #888;
  font-size: 12px;
  margin-top: 4px;
`;

const FormControlLabelMui = styled(FormControlLabel)(`
  .MuiTypography-body1 {
    color: ${COLOR_TEXT_MAIN};
  }
  .Mui-checked {
    color: ${COLOR_TEXT_MAIN};
  }
  span {
    font-weight: 700;
  }
`);

const StyledInputField = withStyles({
  root: {
    width: '100%',
    padding: '8px',
    fontSize: '14px',
    marginRight: '12px',
    border: '1px solid #e6e7e8',
    borderRadius: '4px',
    background: '#fafbfc',
  },
})(TextField);

const AddButton = styled(PaperButton)`
  background-color: white;
  border: 1px solid #ccc;
  color: #333;
`;

const RemoveButton = styled('button')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: none;
  background-color: #fe7877;
  color: white;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  &:hover {
    background-color: #fe9392;
  }
`;

const ButtonArea = styled(StyledPaperItem)`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-bottom: 24px;
`;

const CancelButton = styled(PaperButton)`
  background-color: white;
  border: 1px solid #ccc;
  color: #333;
`;

const SaveButton = styled(PaperButton)``;

const PatternItem = styled('div')`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  width: 100%;
`;

const PatternInput = styled('input')`
  flex: 1;
  padding: 8px;
  font-size: 14px;
  margin-left: 8px;
  border: 1px solid #e6e7e8;
  border-radius: 4px;
  background: #fafbfc;
`;

const PatternList = styled('div')`
  list-style: none;
  padding: 0;
  margin: 8px 0 0 0;
  width: 100%;
`;

const InputError = styled(Typography)`
  color: red;
  font-size: 12px;
  margin-top: 4px;
`;

export default LinkPrefetchEditTab;
